<template>
  <div>
    <SiteNavigationBar />
    <main class="mb-4 mt-[60px] md:mt-[88px] pt-4 w-full">
      <SiteBreadcrumbs
        v-if="!page?.pilarPage || !page.pilarPage.isPillarPage"
        class="px-4 max-lg:max-w-[768px] lg:max-w-[1280px] mx-auto"
        :items="breadcrumbs"
      />
      <div
        :key="'content' + fullWidth"
        class="pt-4 grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-8"
        :class="{
          'px-4 max-lg:max-w-[768px] lg:max-w-[1280px] mx-auto': !fullWidth
        }"
      >
        <SitePageContent
          :class="{ 'lg:col-span-2': fullWidth }"
          :full-width="fullWidth"
          :breadcrumbs="breadcrumbs"
          :page="page"
        />
        <div v-if="!fullWidth" class="flex flex-col items-center gap-5">
          <div
            ref="contactForm"
            class="w-full border border-line-light rounded-lg shadow-xl-right px-3 py-4 text-center"
          >
            <div id="contact-form" class="-mt-[116px] pt-[116px]"></div>
            <h5 class="text-lg font-bold mb-2">
              {{ $t('contactForm.title') }}
            </h5>
            <p class="text-[14px] mb-2 [text-wrap:balance]">
              {{ $t('contactForm.description') }}
            </p>
            <HubspotForm
              element-id="page-form"
              :form-id="{
                en: '47c77108-03dd-47b5-8a71-b68d32f4cfaa',
                es: '6c494fbe-f186-4a01-aab8-b08130dd1a44'
              }"
            />
          </div>
          <div class="banners w-full flex flex-col gap-5">
            <div
              v-for="(banner, index) in sidebarBanners"
              :key="index"
              class="bg-black/50 relative flex flex-col items-center gap-2 md:gap-4 w-full md:aspect-[12/7] max-w-[410px] p-5 rounded-lg overflow-hidden shadow-lg cursor-pointer mx-auto"
              tabindex="0"
              :to="banner.to"
            >
              <h3
                class="text-white drop-shadow text-center font-bold uppercase text-lg pointer-events-none select-none"
              >
                {{ banner.title }}
              </h3>
              <p
                class="text-white drop-shadow text-center text-sm md:text-base px-4 pointer-events-none select-none"
              >
                {{ banner.description }}
              </p>
              <NuxtImg
                v-if="banner?.image"
                :src="banner.image"
                loading="lazy"
                :alt="banner.imageAlt"
                class="absolute z-[-1] left-0 top-0 w-full h-full object-cover object-center"
                provider="custom"
                width="410"
                height="240"
                format="webp"
                fit="scale-down"
                densities="x1 x2"
              />
              <NuxtLink
                class="absolute left-0 top-0 w-full h-full opacity-0 select-none"
                :to="banner.to"
              >
                {{ banner.title }}
              </NuxtLink>
              <AppButton
                class="rounded text-sm md:text-base mt-auto min-h-[36px] md:min-h-[48px] w-[260px] max-w-[75%] py-1"
                :to="banner.to"
              >
                {{ banner.buttonText }}
              </AppButton>
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="false"
        class="flex justify-between items-center pt-4 px-4 max-lg:max-w-[768px] lg:max-w-[1280px] mx-auto"
      >
        <NuxtLink
          v-if="previousPage"
          :to="previousPage.to"
          class="text-primary flex flex-col text-left"
        >
          <span class="text-xs md:text-sm font-bold uppercase">
            {{ $t('common.previous') }}
          </span>
          <span class="text-xs md:text-sm">
            {{ previousPage.title }}
          </span>
        </NuxtLink>
        <NuxtLink
          v-if="nextPage"
          :to="nextPage.to"
          class="text-primary flex flex-col text-right"
        >
          <span class="text-xs md:text-sm font-bold uppercase">
            {{ $t('common.next') }}
          </span>
          <span class="text-xs md:text-sm">
            {{ nextPage.title }}
          </span>
        </NuxtLink>
      </div>
    </main>
    <SiteFooter v-once :fix-locale="locale" />
  </div>
</template>

<script setup lang="ts">
import { GET_WP_PAGE } from '@/utils/queries';
import {
  fixContent,
  fixDataUrl,
  fixImageUrl,
  fixLinkUrl,
  getGraph
} from '@/helpers/fixes';
import { useMainStore } from '@/store';
import type { Page, PillarPage } from '@/types/common';
import { parseMeta } from '@/utils/header';

const HubspotForm = defineAsyncComponent(
  () => import('@/async-components/HubspotForm.vue')
);
const SiteBreadcrumbs = defineAsyncComponent(
  () => import('@/async-components/Site/Breadcrumbs.vue')
);
const SiteNavigationBar = defineAsyncComponent(
  () => import('@/async-components/Site/NavigationBar.vue')
);
const SitePageContent = defineAsyncComponent(
  () => import('@/async-components/Site/Pages/PageContent.vue')
);

type PageFromResponse = {
  title: string;
  content: string;
  elementorContent: string;
  date: string;
  slug: string;
  featuredImage?: {
    node: {
      altText: string;
      sourceUrl: string;
    };
  };
  translations: Record<
    string,
    {
      slug: string;
      uri: string;
      languageCode: string;
    }
  >;
  pilarPage?: PillarPage;
  seo: {
    title: string;
    description: string;
    fullHead: string;
    schema: {
      raw: string;
    };
  };
  nextPage?: {
    title: string;
    slug: string;
  };
  previousPage?: {
    title: string;
    slug: string;
  };
};

type BannerFromResponse = {
  title: string;
  fields: {
    cta: string;
    ctaUrl: string;
    description: string;
    image: {
      altText: string;
      sourceUrl: string;
    };
  };
};

type PageResponse = {
  page: PageFromResponse;
  sidebarBanners: {
    nodes: BannerFromResponse[];
  };
};

const client = useWpClient();

const {
  public: { cloudflareImageProcessing: cfUrl }
} = useRuntimeConfig();

const { locale, t } = useI18n();

const { language } = useLanguage();

const localePath = useLocalePath();

const store = useMainStore();

const props = defineProps<{
  slug: string;
}>();

async function fetch(): Promise<PageResponse> {
  const gqlResponse = await client.query<{
    pages: {
      nodes: [PageFromResponse];
    };
    sidebarBanners: {
      nodes: BannerFromResponse[];
    };
  }>({
    query: GET_WP_PAGE,
    variables: {
      language: locale.value === 'us' ? 'en' : locale.value,
      slug: props.slug
    }
  });

  const page = gqlResponse.data.pages?.nodes?.[0];

  if (!page) {
    showError({
      statusCode: 404,
      message: t('common.pageNotFound'),
      fatal: true
    });

    throw new Error(t('common.pageNotFound'));
  }

  Object.values(page.translations).forEach(translation => {
    if (translation.slug) {
      const localeCode =
        translation.languageCode === 'en' ? 'us' : translation.languageCode;
      store.setTranslationPath(page.slug, translation.slug, localeCode);
    }
  });

  return {
    page,
    sidebarBanners: gqlResponse.data.sidebarBanners
  };
}

const { data: asyncResponse, error: fetchError } =
  await useAsyncData<PageResponse | null>('page', async () => {
    if (process.client) {
      return null;
    }
    return await fetch();
  });

if (fetchError?.value && fetchError.value instanceof Error) {
  console.error(fetchError.value);

  showError({
    statusCode: 404,
    message: t('common.pageNotFound'),
    fatal: true
  });

  throw new Error(t('common.pageNotFound'));
}

const clientResponse = ref<PageResponse | null>(asyncResponse.value);

onMounted(() => {
  if (!asyncResponse.value || !fetchError || !(fetchError instanceof Error)) {
    fetch()
      .then(response => (clientResponse.value = response))
      .catch(fetchError => {
        console.error(fetchError);
        nextTick(() => {
          navigateTo(localePath(`/${t('routes.institutional')}`), {
            replace: true,
            redirectCode: 301
          });
        });
      });
  }
});

const page = computed<Page>(() => {
  const response = clientResponse.value?.page;

  if (!response) {
    return null;
  }

  const image = response.featuredImage?.node?.sourceUrl
    ? fixImageUrl(response.featuredImage?.node?.sourceUrl)
    : null;

  const imageAlt = response.featuredImage?.node?.altText;

  const meta = parseMeta(fixDataUrl(response.seo?.fullHead || ''));

  const seo = response.seo
    ? {
        title: response.seo.title,
        description: response.seo.description,
        schema: response.seo.schema?.raw,
        bodySchemas: [],
        meta
      }
    : null;

  const translations =
    response.translations?.map(node => {
      return {
        slug: node.slug,
        path: localePath(`/${t('routes.institutional')}` + `/${node.slug}`),
        languageCode: node.languageCode
      };
    }) || [];

  let content = response.elementorContent || response.content;

  if (content) {
    const styleTagRegex = /<style[^>]*>[\s\S]*?<\/style>/gi;
    const vcTagRegex = /\[\/?vc_[^\]]+\]/gi;
    content = fixContent(
      content
        .replace(styleTagRegex, '')
        .replace(vcTagRegex, '')
        .replace(/\s+/g, ' '),
      cfUrl
    );

    // extract the content from script tags with type application/ld+json
    const scriptTagRegex =
      /<script[^>]*type="application\/ld\+json"[^>]*>[\s\S]*?<\/script>/gi;
    const scriptTags = content.match(scriptTagRegex);

    if (scriptTags) {
      seo.bodySchemas = scriptTags.map(scriptTag => {
        return scriptTag
          .replace(/<script[^>]*type="application\/ld\+json"[^>]*>/, '')
          .replace(/<\/script>/, '');
      });

      content = content.replace(scriptTagRegex, '');
    }
  }

  return {
    title: response.title,
    content,
    date: new Date(response.date),
    slug: response.slug,
    translations: response.translations,
    image,
    imageAlt,
    seo,
    translations,
    pilarPage: response.pilarPage
  };
});

const sidebarBanners = computed<
  | {
      title: string;
      description: string;
      buttonText: string;
      to: string;
      image: string;
      imageAlt: string;
    }[]
  | null
>(() => {
  const response = clientResponse.value?.sidebarBanners;

  if (!response) {
    return null;
  }

  return response.nodes.map(node => {
    const image = node.fields.image?.sourceUrl
      ? fixImageUrl(node.fields.image.sourceUrl)
      : null;

    const imageAlt = node.fields.image?.sourceUrl;

    return {
      title: node.title,
      description: node.fields.description,
      buttonText: node.fields.cta,
      to: fixLinkUrl(node.fields.ctaUrl),
      image,
      imageAlt
    };
  });
});

const fullWidth = computed(() => {
  const page = clientResponse.value?.page;

  return page?.elementorContent?.length > 0 || page?.pilarPage?.isPillarPage;
});

const nextPage = computed<{
  title: string;
  to: string;
} | null>(() => {
  const page = clientResponse.value?.page;

  if (!page?.nextPage) {
    return null;
  }

  return {
    title: page.nextPage.title,
    to: localePath(`/${t('routes.institutional')}` + `/${page.nextPage.slug}`)
  };
});

const previousPage = computed<{
  title: string;
  to: string;
} | null>(() => {
  const page = clientResponse.value?.page;

  if (!page?.previousPage) {
    return null;
  }

  return {
    title: page.previousPage.title,
    to: localePath(
      `/${t('routes.institutional')}` + `/${page.previousPage.slug}`
    )
  };
});

useSeoExtended({
  title: () => page.value?.seo?.title || page.value?.title,
  description: () => page.value?.seo?.description || '',
  image: () => page.value?.image || '/images/pfs-placeholder.svg'
});

useSchemaOrg(() => {
  return [page.value?.seo?.schema, ...(page.value?.seo?.bodySchemas || [])]
    .filter(Boolean)
    .flatMap(schema => {
      let schemaJson;
      try {
        schemaJson = schema ? JSON.parse(fixDataUrl(schema)) : null;
      } catch (e) {
        console.error(e);
      }

      let graph = getGraph(schemaJson);

      graph = Array.isArray(graph) ? graph : [graph];

      return (Array.isArray(graph) ? graph : [graph]).filter(
        g => g?.['@type'] !== 'BreadcrumbList'
      );
    });
});

useHead(() => ({
  meta: page.value?.seo?.meta || []
}));

const { link } = useCanonicalLink((t, linkLocale) => {
  if (!page.value) {
    return;
  }
  if (locale.value === linkLocale) {
    return localePath(
      `/${t('routes.institutional')}` + `/${page.value?.slug}`,
      linkLocale
    );
  } else if (page.value?.translations[linkLocale]?.slug) {
    const translationSlug = page.value?.translations[linkLocale].slug;
    return localePath(
      `/${t('routes.institutional')}` + `/${translationSlug}`,
      linkLocale
    );
  }
});

const host = useHost();

const breadcrumbs = computed(() => [
  {
    name: t('seo.home'),
    item: host + localePath('/')
  },
  {
    name: t('menu.institutional'),
    item: host + localePath(`/${t('routes.institutional')}`),
    interactuable: false
  },
  {
    name: page.value?.title,
    item: localePath(`/${t('routes.institutional')}` + `/${page.value?.slug}`),
    interactuable: false
  }
]);

useSchemaOrg([
  ...useCommonSchemaOrg(link),
  defineBreadcrumb({
    '@id': link + '#breadcrumb',
    itemListElement: breadcrumbs.value.map(({ name, item }) => ({ name, item }))
  })
]);
</script>
